import variant from '@jitl/quickjs-browser-release-sync-wasm';
import { newQuickJSWASMModuleFromVariant } from 'quickjs-emscripten-core';
import type { QuickJSWASMModule } from 'quickjs-emscripten-core';

type Status = 'pending' | 'error' | 'success';

export const loadQuickJs = (): {
  read: () => QuickJSWASMModule;
} => {
  let status: Status = 'pending';
  let result: QuickJSWASMModule | Error | undefined;
  // Type assertion needed due to library type incompatibility between quickjs-emscripten dependencies
  const suspender = newQuickJSWASMModuleFromVariant(variant as any)
    .then((quickJs) => {
      status = 'success';
      result = quickJs;
    })
    .catch((error) => {
      status = 'error';
      result = error;
    });

  return {
    read() {
      switch (status) {
        case 'pending':
          throw suspender;
        case 'error':
          throw result;
        case 'success':
          return result as QuickJSWASMModule;
        default:
          throw new Error(`Unknown status: ${status}`);
      }
    },
  };
};

// VX-4319: instantiate lazily on first use rather than eagerly at module import.
// The QuickJS module owns a ~17 MB WebAssembly.Memory and the library exposes no
// teardown, so eager init made every viewer realm that loaded the custom-annotation
// chunk allocate (and potentially strand) one even when no custom annotation ever
// rendered. Memoized so it stays a per-realm singleton once created.
let quickJsResource: ReturnType<typeof loadQuickJs> | undefined;

export function useQuickJS(): QuickJSWASMModule {
  quickJsResource ??= loadQuickJs();
  return quickJsResource.read();
}
